import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Paper,
  Grid,
  IconButton,
  ThemeProvider,
  CssBaseline,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { GET_REPORT_BY_USERID_COMPANYID_URL } from "envConfig";
import { Logger } from "utils/Logger";
import { useNavigate } from "react-router-dom";
import { Report as Report_Structure } from "../../Interfaces/AutomateTest/Report";
import { Visibility } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "state/store";
import { useTheme } from "@emotion/react";

export const Report: React.FC = () => {
  const theme = useTheme();
  const [reports, setReports] = useState<Report_Structure[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.authentication.user);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await fetch(GET_REPORT_BY_USERID_COMPANYID_URL + `/?userId=${user._id}&companyId=${user.companyId}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        Logger.error("st-ck-client/src/modules/AutomateTest/Report/Report.ts : fetchReports(): issue while getting Report, response is ", response.statusText);
        setLoading(false);
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      if (Array.isArray(data.data)) {
        setLoading(false);
        const sortedReports = data.data.sort((a: { updatedAt: string | number | Date; }, b: { updatedAt: string | number | Date; }) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        setReports(sortedReports);
      } else {
        Logger.error("st-ck-client/src/modules/AutomateTest/Report/Report.ts : fetchReports(): ", "Unexpected API response format: " + JSON.stringify(data.data, null, 2));
        setLoading(false);
        setReports([]);
      }
    } catch (error) {
      Logger.error("st-ck-client/src/modules/AutomateTest/Report/Report.ts : fetchReports(): ", "Failed to fetch reports: " + error);
      setReports([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);


  return (loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom>
          All Reports
        </Typography>
      </Box>
      {
        <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
          {reports.map((report) => (
            <Grid item xs={12} sm={3.7} key={report._id}>
              <TableContainer component={Paper}>
                <Table sx={{ width: "auto", tableLayout: "auto" }}>
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>TENANT</strong></TableCell>
                      <TableCell>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          {report.tenantId}
                          <IconButton onClick={() => navigate(`/report/${report.tenantId}`)}>
                            <Visibility />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>TESTSUITE</strong></TableCell>
                      <TableCell>{report.testSuiteId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>VERSION</strong></TableCell>
                      <TableCell>{report.versionNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>POD NAME</strong></TableCell>
                      <TableCell>{report.podId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>BROWSER</strong></TableCell>
                      <TableCell>{report.browser}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>STATUS</strong></TableCell>
                      <TableCell>{report.status}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>START TIME</strong></TableCell>
                      <TableCell>{report.testJobStartedAt?.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>END TIME</strong></TableCell>
                      <TableCell>{report.testJobCompletedAt?.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>SUMMARY</strong></TableCell>
                      <TableCell>{"Summary"}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ))}
        </Grid>
      }
    </Box>
  );
};  
